<template>
  <div class="approval-video-modal text-center px-3 pb-4 pt-2">
    <div v-if="uiState === 'creating'" class="py-5">
      <Spinner />
      <p class="small text-muted mt-3">{{ $t("approval_video_creating_session") }}</p>
    </div>

    <div v-else-if="uiState === 'error'" class="text-danger py-3">
      {{ errorMessage }}
    </div>

    <template v-else>
      <div v-if="uiState === 'received'" class="alert alert-success small text-start">
        {{ $t("approval_video_received") }}
      </div>

      <template v-if="uiState === 'waiting'">
        <template v-if="!useWebcamInstead">
          <p class="small text-muted mb-3">{{ $t("approval_video_scan_hint") }}</p>
          <div class="d-flex justify-content-center mb-3">
            <QRCode
              v-if="externalDeviceRoute"
              :key="externalDeviceRoute"
              class="m-auto"
              :text="externalDeviceRoute"
            />
          </div>
          <div class="small text-muted mb-3">
            {{ $t("approval_video_waiting") }}
          </div>
          <button
            type="button"
            class="btn btn-outline-secondary text-uppercase w-100 mb-2"
            @click="useWebcamInstead = true"
          >
            {{ $t("approval_video_use_webcam") }}
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="btn btn-link text-violet text-decoration-underline mb-2 p-0 d-block mx-auto"
            @click="closeWebcamPanel"
          >
            {{ $t("approval_video_back_to_qr") }}
          </button>
          <div class="approval-video-webcam text-start">
            <p class="small text-muted mb-3">{{ $t("approval_video_webcam_hint") }}</p>
            <div v-if="webcamLocalError" class="alert alert-danger small">
              {{ webcamLocalError }}
            </div>
            <div v-if="webcamPhase === 'recording'" class="alert alert-info small mb-3">
              {{ $t("approval_video_webcam_time_remaining", { seconds: webcamRecordingSecondsLeft }) }}
            </div>
            <div v-if="webcamPhase === 'recorded'" class="alert alert-success small mb-3">
              <div class="fw-semibold">{{ $t("approval_video_webcam_recorded") }}</div>
              <div v-if="webcamRecordedMeta" class="text-muted">{{ webcamRecordedMeta }}</div>
            </div>
            <video
              v-if="webcamPreviewUrl"
              class="w-100 rounded bg-dark mb-3 approval-video-preview"
              playsinline
              controls
              :src="webcamPreviewUrl"
            />
            <video
              v-else
              ref="webcamVideoEl"
              class="w-100 rounded bg-dark mb-3"
              playsinline
              muted
              style="max-height: 220px; object-fit: cover"
            />
            <div v-if="webcamPhase === 'uploading'" class="mb-3">
              <div class="progress">
                <div
                  class="progress-bar bg-violet"
                  role="progressbar"
                  :style="{ width: webcamUploadPercent + '%' }"
                  :aria-valuenow="webcamUploadPercent"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {{ webcamUploadPercent }}%
                </div>
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2 justify-content-center">
              <template v-if="webcamPhase === 'idle' || webcamPhase === 'preview'">
                <button
                  type="button"
                  class="btn btn-violet text-uppercase"
                  :disabled="!webcamStreamReady"
                  @click="startWebcamRecording"
                >
                  {{ $t("approval_video_webcam_start") }}
                </button>
              </template>
              <template v-if="webcamPhase === 'recording'">
                <button
                  type="button"
                  class="btn btn-violet text-uppercase"
                  @click="stopWebcamRecording"
                >
                  {{ $t("approval_video_webcam_stop") }}
                </button>
              </template>
              <template v-if="webcamPhase === 'recorded'">
                <button
                  type="button"
                  class="btn btn-outline-violet text-uppercase"
                  @click="retakeWebcam"
                >
                  {{ $t("approval_video_webcam_retake") }}
                </button>
                <button
                  type="button"
                  class="btn btn-violet text-uppercase"
                  :disabled="webcamUploadingLock"
                  @click="sendWebcamRecording"
                >
                  {{ $t("approval_video_webcam_send") }}
                </button>
              </template>
            </div>
          </div>
        </template>
      </template>

      <div v-if="uiState === 'received'" class="d-flex justify-content-center mt-4">
        <Spinner />
      </div>
    </template>
  </div>
</template>

<script>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import QRCode from "@/components/QRCode.vue";
import Spinner from "@/components/general/Spinner";
import { formatFilename } from "@/use/utilities/media/formatFilename";
import { createSession, assignUrl, completeSession } from "@/use/repositories/approvalVideos";
import { error as toastError } from "@/use/toast/error";

const MAX_VIDEO_DURATION_SECONDS = 30;
const MAX_VIDEO_MB = 50;

/** Spatie media UUID only — never session id, filename, or event name. */
function pickMediaUuid(source, sessionId) {
  if (source == null || typeof source !== "object") return null;
  const sid = sessionId != null && sessionId !== "" ? String(sessionId) : null;

  const explicit = [
    source.media_uuid,
    source.approval_video_media_uuid,
    source.media?.uuid,
  ];

  for (const value of explicit) {
    if (value != null && value !== "" && String(value) !== sid) {
      return String(value);
    }
  }

  const generic = source.uuid;
  if (generic != null && generic !== "" && String(generic) !== sid) {
    return String(generic);
  }

  return null;
}

function unwrapPusherPayload(evt) {
  if (evt == null) return null;
  let raw = evt.data !== undefined ? evt.data : evt;

  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    raw.data != null &&
    typeof raw.data === "object" &&
    !Array.isArray(raw.data)
  ) {
    raw = raw.data;
  }

  if (Array.isArray(raw) && raw.length) {
    const first = raw[0];
    return typeof first === "object" && first != null ? first : null;
  }

  if (typeof raw === "object" && raw != null) {
    return raw;
  }

  return null;
}

function pickRecorderMimeType() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const t of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

function extensionFromMime(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "mp4";
  return "webm";
}

function uploadBlobToS3(actionUrl, inputs, blob, filename, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    Object.keys(inputs).forEach((key) => formData.append(key, inputs[key]));
    formData.append("file", blob, filename);

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status === 204 || xhr.status === 200) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    });

    xhr.addEventListener("error", () => reject(new Error("Network error")));

    xhr.open("POST", actionUrl);
    xhr.send(formData);
  });
}

function extractMediaUuidFromComplete(result, sessionId) {
  return pickMediaUuid(result, sessionId);
}

export default {
  name: "ApprovalVideoModal",
  components: { QRCode, Spinner },
  emits: ["confirmed"],
  props: {
    testWorkQueuePhaseId: {
      type: Number,
      required: true,
    },
    testWorkQueueId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();
    const uiState = ref("creating");
    const errorMessage = ref("");
    const sessionId = ref(null);
    const mediaUuid = ref(null);
    const confirmationSubmitted = ref(false);
    const useWebcamInstead = ref(false);
    const channelRef = ref(null);
    const boundHandler = ref(null);

    const webcamVideoEl = ref(null);
    const webcamStreamRef = ref(null);
    const webcamStreamReady = ref(false);
    const webcamPhase = ref("idle");
    const webcamLocalError = ref("");
    const webcamRecordedBlob = ref(null);
    const webcamPreviewUrl = ref("");
    const webcamRecordedSize = ref(0);
    const webcamMediaRecorderRef = ref(null);
    const webcamChunksRef = ref([]);
    const webcamMimeTypeRef = ref("");
    const webcamUploadPercent = ref(0);
    const webcamUploadingLock = ref(false);
    const webcamRecordingSecondsLeft = ref(MAX_VIDEO_DURATION_SECONDS);
    let webcamRecordingInterval = null;
    let webcamRecordingTimeout = null;

    const externalDeviceRoute = computed(() =>
      sessionId.value
        ? `${window.location.origin}/external/device/approval-video/${sessionId.value}`
        : "",
    );

    const canConfirmWithVideo = computed(() => {
      const sid = sessionId.value;
      const mid = mediaUuid.value;
      return mid != null && mid !== "" && String(mid) !== String(sid);
    });

    const webcamRecordedMeta = computed(() => {
      if (!webcamRecordedSize.value) return "";
      const sizeMb = webcamRecordedSize.value / (1024 * 1024);
      return `${i18n.t("approval_video_webcam_recorded_size")}: ${sizeMb.toFixed(1)} MB`;
    });

    const internalInstance = getCurrentInstance();
    const pusher = internalInstance?.appContext?.config?.globalProperties?.$pusher;

    const stopWebcamStream = () => {
      const s = webcamStreamRef.value;
      if (s) {
        s.getTracks().forEach((t) => t.stop());
        webcamStreamRef.value = null;
      }
      webcamStreamReady.value = false;
    };

    const attachWebcamStreamToVideo = () => {
      const el = webcamVideoEl.value;
      const stream = webcamStreamRef.value;
      if (el && stream && !webcamPreviewUrl.value) {
        el.srcObject = stream;
        el.play().catch(() => {});
      }
    };

    const revokeWebcamPreview = () => {
      if (webcamPreviewUrl.value) {
        URL.revokeObjectURL(webcamPreviewUrl.value);
        webcamPreviewUrl.value = "";
      }
    };

    const clearWebcamRecordingTimers = () => {
      if (webcamRecordingInterval) {
        clearInterval(webcamRecordingInterval);
        webcamRecordingInterval = null;
      }
      if (webcamRecordingTimeout) {
        clearTimeout(webcamRecordingTimeout);
        webcamRecordingTimeout = null;
      }
    };

    const resetWebcamChunks = () => {
      clearWebcamRecordingTimers();
      revokeWebcamPreview();
      webcamChunksRef.value = [];
      webcamRecordedBlob.value = null;
      webcamRecordedSize.value = 0;
      webcamRecordingSecondsLeft.value = MAX_VIDEO_DURATION_SECONDS;
    };

    const initWebcamCamera = async () => {
      webcamLocalError.value = "";
      if (!navigator.mediaDevices?.getUserMedia) {
        webcamLocalError.value = i18n.t("approval_video_webcam_no_support");
        return;
      }
      try {
        stopWebcamStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: true,
        });
        webcamStreamRef.value = stream;
        webcamStreamReady.value = true;
        webcamPhase.value = "preview";
        await nextTick();
        attachWebcamStreamToVideo();
      } catch (e) {
        console.error(e);
        webcamLocalError.value = i18n.t("approval_video_webcam_permission_denied");
      }
    };

    watch(useWebcamInstead, (open) => {
      if (open && sessionId.value) {
        resetWebcamChunks();
        webcamMimeTypeRef.value = pickRecorderMimeType();
        if (!webcamMimeTypeRef.value) {
          webcamLocalError.value = i18n.t("approval_video_webcam_no_support");
          webcamPhase.value = "idle";
          return;
        }
        webcamPhase.value = "idle";
        initWebcamCamera();
      } else if (!open) {
        stopWebcamStream();
        const mr = webcamMediaRecorderRef.value;
        if (mr && mr.state !== "inactive") {
          try {
            mr.stop();
          } catch (_) {
            /* noop */
          }
        }
        webcamMediaRecorderRef.value = null;
        webcamPhase.value = "idle";
        webcamLocalError.value = "";
        resetWebcamChunks();
      }
    });

    watch(
      () => webcamVideoEl.value,
      () => attachWebcamStreamToVideo(),
    );

    const closeWebcamPanel = () => {
      useWebcamInstead.value = false;
    };

    const startWebcamRecording = () => {
      webcamLocalError.value = "";
      webcamChunksRef.value = [];
      clearWebcamRecordingTimers();
      webcamRecordingSecondsLeft.value = MAX_VIDEO_DURATION_SECONDS;
      const mime = webcamMimeTypeRef.value;
      let recorder;
      try {
        recorder = new MediaRecorder(webcamStreamRef.value, mime ? { mimeType: mime } : undefined);
      } catch (e) {
        console.error(e);
        webcamLocalError.value = i18n.t("approval_video_webcam_no_support");
        return;
      }
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) webcamChunksRef.value.push(ev.data);
      };
      recorder.onstop = () => {
        clearWebcamRecordingTimers();
        const blobType = mime || "video/webm";
        webcamRecordedBlob.value = new Blob(webcamChunksRef.value, { type: blobType });
        webcamRecordedSize.value = webcamRecordedBlob.value.size;
        if (webcamRecordedSize.value > MAX_VIDEO_MB * 1024 * 1024) {
          webcamLocalError.value = i18n.t("approval_video_mobile_too_large", { max: MAX_VIDEO_MB });
          resetWebcamChunks();
          webcamPhase.value = "preview";
          attachWebcamStreamToVideo();
          return;
        }
        revokeWebcamPreview();
        webcamPreviewUrl.value = URL.createObjectURL(webcamRecordedBlob.value);
        webcamPhase.value = "recorded";
      };
      recorder.start(200);
      webcamMediaRecorderRef.value = recorder;
      webcamPhase.value = "recording";
      webcamRecordingInterval = setInterval(() => {
        webcamRecordingSecondsLeft.value = Math.max(0, webcamRecordingSecondsLeft.value - 1);
      }, 1000);
      webcamRecordingTimeout = setTimeout(() => {
        stopWebcamRecording();
      }, MAX_VIDEO_DURATION_SECONDS * 1000);
    };

    const stopWebcamRecording = () => {
      clearWebcamRecordingTimers();
      const mr = webcamMediaRecorderRef.value;
      if (mr && mr.state !== "inactive") {
        mr.stop();
      }
      webcamMediaRecorderRef.value = null;
    };

    const retakeWebcam = async () => {
      resetWebcamChunks();
      webcamPhase.value = "preview";
      await nextTick();
      attachWebcamStreamToVideo();
    };

    const sendWebcamRecording = async () => {
      const blob = webcamRecordedBlob.value;
      const sid = sessionId.value;
      if (!blob || !sid) return;

      webcamUploadingLock.value = true;
      webcamLocalError.value = "";
      webcamPhase.value = "uploading";
      webcamUploadPercent.value = 0;

      const ext = extensionFromMime(blob.type);
      const namedFile = new File([blob], `approval_webcam.${ext}`, { type: blob.type });
      const friendlyName = formatFilename(namedFile);

      try {
        const { data } = await assignUrl(
          {
            approval_video_session_id: String(sid),
            filename: friendlyName,
          },
          true,
        );

        const inputs = data.inputs;
        const actionUrl = data.attributes?.action;
        if (!inputs || !actionUrl) {
          throw new Error("Invalid signed URL response");
        }

        await uploadBlobToS3(actionUrl, inputs, blob, friendlyName, (pct) => {
          webcamUploadPercent.value = pct;
        });

        const { result } = await completeSession(
          String(sid),
          {
            filename: friendlyName,
          },
          true,
        );

        const mediaId = extractMediaUuidFromComplete(result, sid);
        if (!confirmWithMedia(mediaId)) {
          webcamLocalError.value = i18n.t("approval_video_media_invalid");
          webcamPhase.value = "recorded";
          return;
        }
        useWebcamInstead.value = false;
      } catch (e) {
        console.error(e);
        webcamLocalError.value =
          typeof e?.message === "string" ? e.message : i18n.t("approval_video_mobile_upload_error");
        webcamPhase.value = "recorded";
      } finally {
        webcamUploadingLock.value = false;
      }
    };

    const subscribeToSession = (id) => {
      if (!pusher || !id) return;
      const channelName = "private-live";
      const eventName = `custom_event_${id}`;
      const channel = pusher.subscribe(channelName);
      channelRef.value = channel;

      const handler = (evt) => {
        const payload = unwrapPusherPayload(evt);
        const mediaId = pickMediaUuid(payload, id);
        confirmWithMedia(mediaId);
      };

      channel.bind(eventName, handler);
      boundHandler.value = { eventName, handler };
    };

    const unsubscribe = () => {
      if (!channelRef.value || !boundHandler.value) return;
      channelRef.value.unbind(boundHandler.value.eventName, boundHandler.value.handler);
      channelRef.value = null;
      boundHandler.value = null;
    };

    let bootstrapSeq = 0;

    const bootstrapSession = async () => {
      const seq = ++bootstrapSeq;
      unsubscribe();
      useWebcamInstead.value = false;
      stopWebcamStream();
      uiState.value = "creating";
      errorMessage.value = "";
      sessionId.value = null;
      mediaUuid.value = null;
      confirmationSubmitted.value = false;
      try {
        const { session } = await createSession({
          test_work_queue_phase_id: props.testWorkQueuePhaseId,
          test_work_queue_id: props.testWorkQueueId,
        });
        if (seq !== bootstrapSeq) return;
        const id = session?.id ?? session?.session_id ?? session?.uuid;
        if (!id) {
          throw new Error(i18n.t("approval_video_session_invalid"));
        }
        sessionId.value = id;
        uiState.value = "waiting";
        subscribeToSession(id);
      } catch (e) {
        if (seq !== bootstrapSeq) return;
        console.error(e);
        errorMessage.value = typeof e === "string" ? e : i18n.t("approval_video_session_error");
        uiState.value = "error";
        toastError(errorMessage.value);
      }
    };

    watch(
      () => [props.testWorkQueuePhaseId, props.testWorkQueueId],
      () => bootstrapSession(),
      { immediate: true },
    );

    onBeforeUnmount(() => {
      unsubscribe();
      bootstrapSeq += 1;
      stopWebcamStream();
      resetWebcamChunks();
      clearWebcamRecordingTimers();
      const mr = webcamMediaRecorderRef.value;
      if (mr && mr.state !== "inactive") {
        try {
          mr.stop();
        } catch (_) {
          /* noop */
        }
      }
    });

    const emitConfirm = () => {
      const sid = sessionId.value;
      const mid = mediaUuid.value;

      if (confirmationSubmitted.value) return;

      if (process.env.NODE_ENV === "development") {
        console.debug("[approval-video] confirm", { sessionId: sid, mediaUuid: mid });
      }

      if (!canConfirmWithVideo.value) {
        toastError(i18n.t("approval_video_media_invalid"));
        return;
      }

      confirmationSubmitted.value = true;
      emit("confirmed", {
        approval_video_session_id: sid,
        approval_video_media_uuid: mid,
      });
    };

    const confirmWithMedia = (mediaId) => {
      if (!mediaId || String(mediaId) === String(sessionId.value)) return false;
      mediaUuid.value = String(mediaId);
      uiState.value = "received";
      emitConfirm();
      return true;
    };

    return {
      uiState,
      errorMessage,
      externalDeviceRoute,
      useWebcamInstead,
      sessionId,
      webcamVideoEl,
      webcamPhase,
      webcamStreamReady,
      webcamLocalError,
      webcamPreviewUrl,
      webcamRecordedMeta,
      webcamRecordingSecondsLeft,
      webcamUploadPercent,
      webcamUploadingLock,
      closeWebcamPanel,
      startWebcamRecording,
      stopWebcamRecording,
      retakeWebcam,
      sendWebcamRecording,
    };
  },
};
</script>

<style scoped>
.approval-video-preview {
  max-height: 260px;
  object-fit: contain;
}
</style>
